<template>
  <EDropdown
    :visible="visible"
    class="personal-counter-dropdown"
  >
    <div
      class="personal-counter-dropdown__header"
      data-test-id="personal-counter-dropdown-header"
    >
      <div class="personal-counter-dropdown__searches">
        <EIcon
          icon="search"
          size="s"
          class="personal-counter-dropdown__icon"
        />
        <span>
          {{ searches }} {{ searchesText }}
        </span>
      </div>
      <div class="personal-counter-dropdown__trees">
        <span>
          {{ trees }}
        </span>
        <EIcon
          icon="tree-color"
          size="s"
          class="personal-counter-dropdown__icon"
        />
      </div>
    </div>

    <div
      v-if="title"
      class="personal-counter-dropdown__title"
    >
      {{ title }}
    </div>
    <p
      v-safe-html="text"
      class="personal-counter-dropdown__text"
      data-test-id="personal-counter-dropdown-text"
    />

    <EDropdownExperimentContent v-if="isExperimentEnabled" />

    <div
      class="personal-counter-dropdown__actions"
    >
      <ELink
        v-if="!loggedIn"
        href="/account/login"
        data-test-id="personal-counter-dropdown-signin"
        data-track-id="personal-counter-content-signin"
        rel="noopener"
        @click="trackLogin"
      >
        {{ $t('common.header.counter.loggedout.cta') }}
      </ELink>
      <ELink
        v-else
        as="span"
        data-test-id="personal-counter-dropdown-signout"
        data-track-id="personal-counter-content-signout"
        @click="signOutUser"
      >
        {{ $t('common.header.counter.loggedin.cta') }}
      </ELink>
      <ELink
        :href="learnMoreLink"
        target="_blank"
        rel="noreferrer"
        data-test-id="personal-counter-dropdown-info"
        :data-track-id="learnMoreTrackId"
        @click="trackLearnMore"
      >
        {{ $t('common.header.counter.howitworks') }}
      </ELink>
    </div>
  </EDropdown>
</template>

<script>
import { mapState } from 'vuex';
import EcosiaUser from '@ecosia/ecosia-user';

import { sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import { getCookieOptions } from '~~/common/js/universal/utils';
import {
  MAIN as MAIN_COOKIE,
  HEIMDALL as HEIMDALL_COOKIE,
} from '~~/common/js/constants/cookies';
import { HELPSCOUT_LEARN_MORE_SYNC_COUNTER } from '~~/common/js/constants/urls';
import EDropdown from '~~/common/vue/components/dropdown';
import EIcon from '~~/common/vue/components/icon';
import ELink from '~~/common/vue/components/link';
import EDropdownExperimentContent from './dropdown-experiment-content';

export default {
  name: 'EPersonalCounterDropdown',
  components: {
    EDropdown,
    EDropdownExperimentContent,
    EIcon,
    ELink,
  },
  props: {
    trees: {
      type: String,
      required: true,
    },
    visible: {
      type: Boolean,
      required: true,
    },
    isExperimentEnabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('user', [
      'loggedIn',
      'loginFailed',
      'treeCount',
    ]),
    learnMoreLink() {
      return HELPSCOUT_LEARN_MORE_SYNC_COUNTER;
    },
    searches() {
      return this.treeCount.toLocaleString();
    },
    searchesText() {
      const prefix = 'common.header.counter.search';
      const translation = this.treeCount === 1 ? prefix : prefix.concat('es');
      return this.$t(translation);
    },
    title() {
      if (this.loginFailed) {
        return this.$t('common.generic.error');
      }

      if (this.loggedIn) {
        return this.$t('common.header.counter.loggedin.title');
      }

      return null;
    },
    text() {
      if (this.loginFailed) {
        return this.$t('common.generic.tryagain');
      }

      const baseKey = 'common.header.counter';
      const loggedinKey = `${baseKey}.loggedin.body`;
      const loggedoutKey = `${baseKey}.loggedout.body`;

      if (this.loggedIn) {
        return this.$t(loggedinKey);
      }
      if (this.isExperimentEnabled) {
        // hardcoded text since it does not need translations and will be removed after experiment
        return "This is the number of trees you've planted with Ecosia. The more you search – the more trees we plant.";
      }
      return this.$t(loggedoutKey);
    },
    learnMoreTrackId() {
      const suffix = this.loggedIn ? 'loggedin' : 'loggedout';
      return `personal-counter-content-learn-more--${suffix}`;
    },
  },
  methods: {
    signOutUser() {
      // Set the cookie to the hostname, plus a leading dot, minus any port
      // eslint-disable-next-line compat/compat
      const domain = `.${window.location.hostname.split(':')[0]}`;
      const options = getCookieOptions({ domain });
      const cookieString = this.$cookieManager.get(MAIN_COOKIE);
      const user = new EcosiaUser({ cookieString });

      this.$cookieManager.remove(HEIMDALL_COOKIE, options);
      user.set('treeCount', 0);
      window.location.reload(true);
    },
    trackLogin() {
      sendCoreAnalyticsEvent('personalCounterClickLogin');
    },
    trackLearnMore() {
      sendCoreAnalyticsEvent('personalCounterClickLearnMore', { loggedIn: this.loggedIn });
    },
  },
};
</script>

<style lang="scss" scoped>

.personal-counter-dropdown {
  font-size: $font-m;
  line-height: $line-height-m;
  width: 300px;
  background-color: var(--color-background-elevation-1);
  color: var(--color-text-primary);

  @include tablet {
    @include elevation(2);
  }
}

.personal-counter-dropdown__header {
  padding: $space-m $space-m 0;
  display: flex;
  justify-content: space-between;
}

.personal-counter-dropdown__searches,
.personal-counter-dropdown__trees {
  display: flex;
  align-content: center;
  gap: $space-1s;
}

.personal-counter-dropdown__trees {
  font-weight: $font-weight-1l;
}

.personal-counter-dropdown__icon {
  color: var(--color-brand-primary);
}

.personal-counter-dropdown__title {
  font-weight: $font-weight-1l;
  padding: $space-s $space-m 0;
}

.personal-counter-dropdown__text {
  margin: 0;
  padding: $space-1s $space-m $space-s;
}

.personal-counter-dropdown__actions {
  @include divider('top');

  padding: $space-s $space-m;
  display: flex;
  justify-content: space-between;
}
</style>
